import React, { useState } from 'react';
import { BhiDialog } from 'src/UIKit/components/Dialog';
import { DialogContent, IconButton, Box, Typography, Button } from '@mui/material';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import DescriptionIcon from '@mui/icons-material/Description';
import EventIcon from '@mui/icons-material/Event';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { Close as CloseIcon } from '@mui/icons-material';
import VcfDisplayFromUrl from 'src/components/VcfViewer/view';

function canPreview(extOrMime?: string): boolean {
  if (!extOrMime) return true; // Default case assumes it's previewable

  return (
    extOrMime.startsWith('image/') ||
    extOrMime.startsWith('video/') ||
    extOrMime.startsWith('audio/') ||
    extOrMime === 'text/vcard' ||
    extOrMime === 'text/x-vcard' ||
    extOrMime === 'text/calendar' ||
    // File extensions
    ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'mp4', '3gp', 'm4a', 'vcf', 'ics'].includes(
      extOrMime.toLowerCase(),
    )
  );
}

function renderFileIconByExtensionOrMime(options: {
  url: string;
  extOrMime?: string;
  width?: string;
  onPreviewClick?: () => void;
}): React.JSX.Element {
  const w = options.width ? options.width : '100%';
  const isPreviewable = canPreview(options.extOrMime);

  switch (options.extOrMime) {
    case 'vcf':
    case 'text/vcard':
    case 'text/x-vcard':
      return (
        <ContactPhoneIcon
          sx={{ fontSize: 40, color: 'text.secondary', cursor: 'pointer' }}
          onClick={options.onPreviewClick}
        />
      );
    case 'txt':
    case 'text/plain':
    case 'pdf':
    case 'application/pdf':
      return <DescriptionIcon sx={{ fontSize: 40, color: 'text.secondary' }} />;
    case 'ics':
    case 'text/calendar':
      return (
        <EventIcon
          sx={{ fontSize: 40, color: 'text.secondary', cursor: 'pointer' }}
          onClick={options.onPreviewClick}
        />
      );
    case 'm4a':
    case 'audio/x-m4a':
      return (
        <AudioFileIcon
          sx={{ fontSize: 40, color: 'text.secondary', cursor: 'pointer' }}
          onClick={options.onPreviewClick}
        />
      );
    case '3pg':
    case 'video/3gpp':
    case 'mp4':
    case 'video/mp4':
      return (
        <VideoFileIcon
          sx={{ fontSize: 40, color: 'text.secondary', cursor: 'pointer' }}
          onClick={options.onPreviewClick}
        />
      );
    default:
      return (
        <img
          width={w}
          height="auto"
          src={options.url}
          alt="File"
          style={{
            cursor: isPreviewable ? 'pointer' : 'default',
          }}
          onClick={isPreviewable ? options.onPreviewClick : undefined}
        />
      );
  }
}

const ChatFileIcon = ({
  url,
  mimeType,
  width,
}: {
  url: string;
  mimeType?: string | null;
  width?: string;
}) => {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const handlePreviewClick = () => {
    setIsPreviewModalOpen(true);
  };

  const getFileNameFromUrl = (url: string): string | undefined => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.split('/').pop() || undefined;
    } catch {
      return url.split('/').pop() || undefined;
    }
  };

  const extOrMime =
    mimeType ||
    (() => {
      const match = /\.(.{0,5})$/.exec(url);
      return match?.[1];
    })();

  const fileName = getFileNameFromUrl(url);

  return (
    <>
      {renderFileIconByExtensionOrMime({
        url,
        extOrMime,
        width,
        onPreviewClick: handlePreviewClick,
      })}

      {/* Inbox Preview Modal - Clean Design */}
      <BhiDialog
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <IconButton
          onClick={() => setIsPreviewModalOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: t => t.palette.grey[500],
            zIndex: 1,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            {extOrMime?.startsWith('image/') ||
            ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
              extOrMime?.toLowerCase() || '',
            ) ? (
              <img
                src={url}
                alt={fileName || 'Asset'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            ) : extOrMime === 'text/vcard' ||
              extOrMime === 'text/x-vcard' ||
              extOrMime === 'vcf' ? (
              <Box sx={{ p: 2 }}>
                <VcfDisplayFromUrl vcfUrl={url} />
              </Box>
            ) : extOrMime?.startsWith('video/') ||
              ['mp4', '3gp'].includes(extOrMime?.toLowerCase() || '') ? (
              <video
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                }}
              >
                <source
                  src={url}
                  type={extOrMime?.startsWith('video/') ? extOrMime : `video/${extOrMime}`}
                />
              </video>
            ) : extOrMime?.startsWith('audio/') ||
              ['m4a'].includes(extOrMime?.toLowerCase() || '') ? (
              <Box>
                <audio controls style={{ width: '100%', marginBottom: '16px' }}>
                  <source
                    src={url}
                    type={extOrMime?.startsWith('audio/') ? extOrMime : `audio/${extOrMime}`}
                  />
                </audio>
                <Typography variant="body2">Audio file: {fileName}</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 4 }}>
                <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {fileName || 'File'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No preview available for this file type.
                </Typography>
                <Button variant="contained" href={url} download={fileName} sx={{ mt: 2 }}>
                  Download File
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </BhiDialog>
    </>
  );
};

export default ChatFileIcon;
